import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { KPIData } from "@/shared/models/KpiData";
import {
	DmhEvent,
	GapTimeInterval,
	M1ShiftReport,
	PushRateCrewData,
	RollingTimeData,
	ShiftReportDelay,
	ShiftReportGanttDelay,
	ShiftReportHeat,
	ShiftReportProduction,
	ShiftReportProductionSummary,
	ShiftReportRollChange,
	ShiftReportStats,
} from "../models/M1ShiftReportResponse";

const moment = getMomentTz();

export const getM1ShiftReport = async (
	params: { shiftId: number },
	signal?: AbortSignal,
): Promise<M1ShiftReport> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ShiftId", value: params.shiftId },
	];

	const resp = await apiService.callV2(
		"[M1].[GetShiftReport]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const tables = get(resp, "data.tables", {});

		// RS1: Shift Stats (TotalAdjustments always 0 from SP)
		const shiftStatsRows = get(tables, "0.rows", []) as ShiftReportStats[];
		const shiftStats = shiftStatsRows.length > 0 ? shiftStatsRows[0] : null;

		// RS2: KPI Data
		const kpiDataRows = get(tables, "1.rows", []) as KPIData[];
		const kpiData = kpiDataRows.length > 0 ? kpiDataRows[0] : null;

		// RS3: Gantt Production
		const ganttProduction = get(
			tables,
			"2.rows",
			[],
		) as ShiftReportProduction[];
		const formattedProduction = ganttProduction.map((gp) => ({
			...gp,
			Start: gp.Start ? moment.utc(gp.Start).toDate() : null,
			End: gp.End ? moment.utc(gp.End).toDate() : null,
		}));

		// RS4: Gantt Delays
		const ganttDelays = get(tables, "3.rows", []) as ShiftReportGanttDelay[];
		const formattedDelays = ganttDelays.map((gd) => ({
			...gd,
			Start: gd.Start ? moment.utc(gd.Start).toDate() : null,
			End: gd.End ? moment.utc(gd.End).toDate() : null,
		}));

		// RS5: Heats
		const heats = get(tables, "4.rows", []) as ShiftReportHeat[];
		const formattedHeats = heats.map((heat) => ({
			...heat,
			Start: heat.Start ? moment.utc(heat.Start).toDate() : null,
			End: heat.End ? moment.utc(heat.End).toDate() : null,
		}));

		// RS6: Top 5 Delays
		const delays = get(tables, "5.rows", []) as ShiftReportDelay[];

		// RS7: Production Summary
		const productionSummary = get(
			tables,
			"6.rows",
			[],
		) as ShiftReportProductionSummary[];
		const formattedProductionSummary = productionSummary.map((ps) => ({
			...ps,
			Start: ps.Start ? moment.utc(ps.Start).toDate() : null,
			End: ps.End ? moment.utc(ps.End).toDate() : null,
		}));

		// RS8: Rolling Time Data
		const rollingTimeRows = get(tables, "7.rows", []) as RollingTimeData[];
		const rollingTimeRaw =
			rollingTimeRows.length > 0 ? rollingTimeRows[0] : null;
		const rollingTime = rollingTimeRaw
			? {
					...rollingTimeRaw,
					ExpectedRollOutTime: rollingTimeRaw.ExpectedRollOutTime
						? moment.utc(rollingTimeRaw.ExpectedRollOutTime).toDate()
						: null,
				}
			: null;

		// RS9: Gap Time Intervals (gaps between production and delays)
		const gapTimeIntervals = get(tables, "8.rows", []) as GapTimeInterval[];
		const formattedGapTime = gapTimeIntervals.map((dt) => ({
			...dt,
			Start: dt.Start ? moment.utc(dt.Start).toDate() : null,
			End: dt.End ? moment.utc(dt.End).toDate() : null,
		}));

		// RS10: Roll Change Intervals
		const rollChangeRaw = get(tables, "9.rows", []) as ShiftReportRollChange[];
		const rollChangeIntervals = rollChangeRaw.map((rc) => ({
			...rc,
			Start: rc.Start ? moment.utc(rc.Start).toDate() : null,
			End: rc.End ? moment.utc(rc.End).toDate() : null,
		}));

		// RS11: D/M/H Events
		const dmhEventsRaw = get(tables, "10.rows", []) as DmhEvent[];
		const dmhEvents = dmhEventsRaw.map((e) => ({
			...e,
			Start: e.Start ? moment.utc(e.Start).toDate() : null,
			End: e.End ? moment.utc(e.End).toDate() : null,
		}));

		// RS12: Push Rate by Crew
		const pushRateData = get(tables, "11.rows", []) as PushRateCrewData[];

		return {
			ShiftStats: shiftStats,
			KpiData: kpiData,
			GanttProduction: formattedProduction,
			GanttDelays: formattedDelays,
			GanttGapTime: formattedGapTime,
			Heats: formattedHeats,
			Delays: delays,
			ProductionSummary: formattedProductionSummary,
			RollingTime: rollingTime,
			RollChangeIntervals: rollChangeIntervals,
			DmhEvents: dmhEvents,
			PushRateData: pushRateData,
		};
	}

	throw new Error(resp.message || "Error fetching M1 shift report data");
};
